import { useState, useEffect, useRef, useCallback } from 'react';
import { useApi } from './useApi';

const SCAN_STATUS_POLL_INTERVAL_MS = 5_000;

export interface ScadaScanProgress {
  status: 'idle' | 'scanning' | 'clean' | 'malicious';
  maliciousFiles: string[];
}

const IDLE_PROGRESS: ScadaScanProgress = { status: 'idle', maliciousFiles: [] };

/**
 * Polls GET /storage/screenshots/scan-status/:batchId until the malware scan
 * for a SCADA screenshot upload resolves — Clean or Malicious. Unlike meter-data's
 * useScanStatusPoll, there's no ADF pipeline/runId to wait on: once the backend
 * reports Clean, the caller is free to persist the SCADA_upload record immediately.
 */
export function useScadaScanStatusPoll(batchId: string | null) {
  const [scanProgress, setScanProgress] = useState<ScadaScanProgress>(IDLE_PROGRESS);
  const { checkScadaScanStatus } = useApi();
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const resetScan = useCallback(() => {
    stopPolling();
    setScanProgress(IDLE_PROGRESS);
  }, [stopPolling]);

  useEffect(() => {
    if (!batchId) return undefined;

    setScanProgress({ status: 'scanning', maliciousFiles: [] });

    const poll = async () => {
      try {
        const response = await checkScadaScanStatus(batchId);
        const data = response?.data;
        if (!data) return;

        if (data.status === 'Clean') {
          setScanProgress({ status: 'clean', maliciousFiles: [] });
          stopPolling();
        } else if (data.status === 'Malicious') {
          setScanProgress({ status: 'malicious', maliciousFiles: data.maliciousFiles ?? [] });
          stopPolling();
        }
        // PendingScan: keep polling.
      } catch {
        // Transient failure — keep polling until the interval retries.
      }
    };

    poll();
    pollRef.current = setInterval(poll, SCAN_STATUS_POLL_INTERVAL_MS);

    return () => stopPolling();
  }, [batchId, checkScadaScanStatus, stopPolling]);

  return { scanProgress, resetScan };
}
